/**
 * Per-context Right Panel card definitions for AdminClient (cabinet portal).
 *
 * #RIGHTRAIL-honesty FIX (AUDIT_MOCK_STUB P1): this file previously shipped a
 * fully HARDCODED per-context rail — fake agenda ("Comité Mensuel Risques Mer.
 * 14h"), fake messages/clients ("Client TechCo", "Acme Corp"), fabricated
 * opportunities ("BeauPort SA 85k€"), findings ("A.5.1"), incidents
 * ("CVE-2026-XXXX") and KPI chips (MRR "184 320 €", occupation 78%, coverage
 * 86%, security 74/100, data quality 92%) — rendered on EVERY page for EVERY
 * cabinet user, so fabricated figures looked like real live cabinet data.
 *
 * There is no per-tenant cabinet right-rail summary endpoint to source these
 * numbers, so we no longer fabricate them. Each context's rail is now a set of
 * honest NAVIGATIONAL quick-links (no numbers, no client names, no fake
 * records) pointing to the real cabinet pages. Swap in `kpi`/`list` cards fed
 * by a real API/store here if/when a cabinet summary endpoint is added.
 */

import {
  TrendingUp, Briefcase,
  ClipboardCheck, Activity, Shield, Sparkles, Building2,
  GraduationCap, BadgeCheck,
} from 'lucide-react'
import type { GrcContextId } from '@/config/contexts'
import { tn } from '@/i18n/navLabels'
import type { RightPanelCard } from '@/components/layout/RightPanel'

// Shared quick-links for every cabinet context (real destinations, no fake data).
const DEFAULT_CARDS: RightPanelCard[] = [
  {
    kind: 'list',
    id: 'quick-links',
    title: 'Accès rapides',
    icon: Sparkles,
    accent: 'blue',
    items: [
      { id: 'ql-clients', primary: 'Clients', href: '/clients' },
      { id: 'ql-mandats', primary: 'Mandats', href: '/mandats' },
      { id: 'ql-calendar', primary: 'Calendrier', href: '/global/calendar' },
      { id: 'ql-messages', primary: 'Messages', href: '/global/messages' },
    ],
  },
]

const ASSOCIE_CARDS: RightPanelCard[] = [
  {
    kind: 'list',
    id: 'associe-links',
    title: 'Développement cabinet',
    icon: TrendingUp,
    accent: 'amber',
    items: [
      { id: 'a1', primary: 'Prospection', href: '/prospection' },
      { id: 'a2', primary: 'Offres commerciales', href: '/offers' },
      { id: 'a3', primary: 'Portefeuille clients', href: '/clients' },
      { id: 'a4', primary: 'Facturation', href: '/billing' },
    ],
  },
  ...DEFAULT_CARDS,
]

const DIRECTEUR_CARDS: RightPanelCard[] = [
  {
    kind: 'list',
    id: 'directeur-links',
    title: 'Pilotage missions',
    icon: Briefcase,
    accent: 'indigo',
    items: [
      { id: 'd1', primary: 'Mandats & missions', href: '/mandats' },
      { id: 'd2', primary: 'Équipe', href: '/team' },
      { id: 'd3', primary: 'Constats d\'audit', href: '/audit-pro/findings' },
    ],
  },
  ...DEFAULT_CARDS,
]

const AUDITEUR_CARDS: RightPanelCard[] = [
  {
    kind: 'list',
    id: 'auditeur-links',
    title: 'Audit',
    icon: ClipboardCheck,
    accent: 'teal',
    items: [
      { id: 'au1', primary: 'Constats', href: '/audit-pro/findings' },
      { id: 'au2', primary: 'Préparation certification', href: '/audit-pro/cert-readiness' },
      { id: 'au3', primary: 'Mandats', href: '/mandats' },
    ],
  },
  ...DEFAULT_CARDS,
]

const CONSULTANT_CARDS: RightPanelCard[] = [
  {
    kind: 'list',
    id: 'consultant-links',
    title: 'GRC',
    icon: Activity,
    accent: 'purple',
    items: [
      { id: 'co1', primary: 'Mandats', href: '/mandats' },
      { id: 'co2', primary: 'Clients', href: '/clients' },
      { id: 'co3', primary: 'Constats', href: '/audit-pro/findings' },
    ],
  },
  ...DEFAULT_CARDS,
]

const SECURITE_CARDS: RightPanelCard[] = [
  {
    kind: 'list',
    id: 'securite-links',
    title: 'Sécurité',
    icon: Shield,
    accent: 'red',
    items: [
      { id: 'se1', primary: 'Score sécurité', href: '/security-pro/score' },
      { id: 'se2', primary: 'Incidents', href: '/security-pro/incidents' },
      { id: 'se3', primary: 'Mandats', href: '/mandats' },
    ],
  },
  ...DEFAULT_CARDS,
]

const DONNEES_CARDS: RightPanelCard[] = [
  {
    kind: 'list',
    id: 'donnees-links',
    title: 'Données & IA',
    icon: BadgeCheck,
    accent: 'green',
    items: [
      { id: 'do1', primary: 'Qualité des données', href: '/data-pro/quality' },
      { id: 'do2', primary: 'Registre des modèles IA', href: '/ai-ia-pro/registre' },
      { id: 'do3', primary: 'Mandats', href: '/mandats' },
    ],
  },
  ...DEFAULT_CARDS,
]

const ANALYSTE_CARDS: RightPanelCard[] = [
  {
    kind: 'list',
    id: 'analyste-links',
    title: 'Analyse',
    icon: TrendingUp,
    accent: 'blue',
    items: [
      { id: 'an1', primary: 'Clients', href: '/clients' },
      { id: 'an2', primary: 'Mandats', href: '/mandats' },
      { id: 'an3', primary: 'Constats', href: '/audit-pro/findings' },
    ],
  },
  ...DEFAULT_CARDS,
]

const FORMATEUR_CARDS: RightPanelCard[] = [
  {
    kind: 'list',
    id: 'formateur-links',
    title: 'Formation',
    icon: GraduationCap,
    accent: 'amber',
    items: [
      { id: 'fo1', primary: 'Programmes de formation', href: '/change-pro/formation' },
      { id: 'fo2', primary: 'Campagnes de maturité', href: '/maturity-pro/campaigns' },
      { id: 'fo3', primary: 'Clients', href: '/clients' },
    ],
  },
  ...DEFAULT_CARDS,
]

const ADMIN_CARDS: RightPanelCard[] = [
  {
    kind: 'list',
    id: 'admin-links',
    title: 'Administration',
    icon: Building2,
    accent: 'slate',
    items: [
      { id: 'ad1', primary: 'Équipe', href: '/team' },
      { id: 'ad2', primary: 'Facturation', href: '/billing' },
      { id: 'ad3', primary: 'Clients', href: '/clients' },
    ],
  },
  ...DEFAULT_CARDS,
]

// ── i18n: localize card titles + list-item labels at the choke-point ────────
function localizeCards(cards: RightPanelCard[]): RightPanelCard[] {
  return cards.map((card) => {
    const next: RightPanelCard = { ...card, title: tn(card.title) }
    if (next.kind === 'list' && Array.isArray((next as any).items)) {
      ;(next as any).items = (next as any).items.map((it: any) => ({
        ...it,
        primary: tn(it.primary),
        ...(it.secondary ? { secondary: tn(it.secondary) } : {}),
      }))
    }
    return next
  })
}

export function getRightPanelCardsForContext(
  contextId: GrcContextId,
): RightPanelCard[] {
  switch (contextId) {
    case 'associe': return localizeCards(ASSOCIE_CARDS)
    case 'directeur-mission': return localizeCards(DIRECTEUR_CARDS)
    case 'consultant-grc': return localizeCards(CONSULTANT_CARDS)
    case 'expert-securite': return localizeCards(SECURITE_CARDS)
    case 'expert-donnees-ia': return localizeCards(DONNEES_CARDS)
    case 'auditeur': return localizeCards(AUDITEUR_CARDS)
    case 'analyste': return localizeCards(ANALYSTE_CARDS)
    case 'formateur': return localizeCards(FORMATEUR_CARDS)
    case 'administration': return localizeCards(ADMIN_CARDS)
    default: return localizeCards(DEFAULT_CARDS)
  }
}
